import React, { useState } from 'react';
import ExpandSection from '../ExpandSection';
import Button from '../form/Button';
import { ActionsModal } from '../modals';
import { EventKind, ObjectKind, useEventsQuery } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty } from '../GenericTable';
import { useEventsColumns } from './useEventsColumns';
import { TableIds } from 'src/api/types/misc';
import { Poppins, TableSkeleton } from 'src/common';
import { isQueryLoading } from 'src/graphql/client';
import _ from 'lodash';
import useVersion from 'src/hooks/useVersion';
import Filters from './filters';
import { FiltersType } from './filters/state';
import { ExportCSV } from './comps';

interface ActionsSectionProps {
  objectType: ObjectKind;
  name: string;
  objectId: string;
}

const ActionsSection: React.FC<ActionsSectionProps> = ({ objectType, objectId, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modal, setModal] = useState<{ isOpen: boolean; eventId: string | null }>({ isOpen: false, eventId: null });
  const { workspaceId, features } = useAuth();
  const { version } = useVersion();
  const [appliedFilters, setAppliedFilters] = useState<FiltersType | undefined>(undefined);

  const { data, loading, error, networkStatus } = useEventsQuery({
    variables: {
      workspaceId: workspaceId,
      eventType: EventKind.Action,
      objectType,
      objectId,
    },
    skip: !isOpen || !features['app.events'],
  });
  const isEventsLoading = isQueryLoading({ loading, data, networkStatus });

  const { columns, GTColumnSelectAnchorExported } = useEventsColumns({
    objectId,
    data: data?.events,
    setEventId: (eventId) => setModal({ isOpen: true, eventId }),
    hasVersion: !!version,
  });

  if (!features['app.events']) {
    return null;
  }

  return (
    <>
      {modal.isOpen && (
        <ActionsModal
          onClose={() => setModal({ isOpen: false, eventId: null })}
          objectType={objectType}
          name={name}
          objectId={objectId}
          eventId={modal.eventId}
          eventIds={data?.events.map((el) => el.id) ?? []}
        />
      )}
      <ExpandSection
        title="ACTIONS"
        sectionId={`${objectType}_actions`}
        onChange={setIsOpen}
        endComponent={
          isOpen && (
            <div css="display: flex; grid-gap: 16px;">
              {/* <Filters ref={setAppliedFilters} /> */}
              {!!data?.events?.length && <ExportCSV events={data.events} name={name} />}
              {!version ? (
                <Button
                  primary
                  data-cy="add-actions-btn"
                  onClick={() => setModal({ isOpen: true, eventId: null })}
                  $constWidth={200}
                >
                  + ADD ACTIONS
                </Button>
              ) : undefined}
            </div>
          )
        }
      >
        <div>
          {isEventsLoading && <TableSkeleton />}
          {error && (
            <Poppins color="error" px={14}>
              {error.message}
            </Poppins>
          )}
          {data?.events && (
            <GenericTable
              tableId={TableIds.events}
              data={_.orderBy(data?.events, 'meta.createdAt', 'desc')}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              searchable
              emptyRow={
                <GTRowEmpty
                  text={genEmtyRowTextComponent({
                    onClick: () => setModal({ isOpen: true, eventId: null }),
                    endText: 'to add actions.',
                  })}
                />
              }
            />
          )}
        </div>
      </ExpandSection>
    </>
  );
};

export default ActionsSection;
